<template class="import-post-tags">
  <div>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions
          class="import-post-tags__close-button"
        >
          <DsButton
            v-tooltip="closeLabel"
            #top-right
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="import-post-tags__modal-style">
          <h3>{{ this.$t('components.ImportPostTags.faq.title') }}</h3>
          <p>{{ this.$t('components.ImportPostTags.faq.description') }}</p>

          <h3>{{ this.$t('components.ImportPostTags.faq2.title') }}</h3>
          <p>{{ this.$t('components.ImportPostTags.faq2.description') }}</p>

          <h3>{{ this.$t('components.ImportPostTags.faq3.title') }}</h3>
          <p>{{ this.$t('components.ImportPostTags.faq3.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :open-dialog="openDialog"
      :item-id="itemId"
      :has-filter="false"
      :has-data-to-export="false"
      v-bind="$attrs"
    />
    <v-form
      ref="form"
      v-model="valid"
    >
      <v-row>
        <v-col>
          <template>
            <v-file-input
              v-model="file"
              accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xls, application/vnd.ms-excel"
              :disabled="isSending"
              :rules="rules.isValue"
              :placeholder="this.$t('components.ImportPostTags.label.insertFile')"
              :label="$t('components.ImportPostTags.label.file')"
              prepend-icon="mdi-paperclip"
              required
            >
              <template v-slot:selection="{ text }">
                <v-chip
                  small
                  label
                  color="primary"
                >
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </template>
        </v-col>
      </v-row>
    </v-form>
    <v-row
      no-gutters
      justify="end"
    >
      <DsButton
        :disabled="disableButton"
        :loading="isSending"
        color="primary"
        rounded
        large
        @click="postData"
      >
        {{ $t("components.ImportPostTags.label.send") }}
      </DsButton>
      <DsButton
        :disabled="isSending"
        color="secondary"
        class="ml-3"
        rounded
        large
        @click="resetForm"
      >
        {{ $t('components.ImportPostTags.label.clear') }}
      </DsButton>
    </v-row>
    <v-card v-if="success">
      <v-card-text class="import-post-tags__card-content">
        <v-list-item class="card-content__item">
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.ImportPostTags.label.status') }}
            </span>
            {{ $t('components.ImportPostTags.label.processed') }}
          </div>
        </v-list-item>
      </v-card-text>
    </v-card>
    <v-card v-if="error && errorMessage">
      <v-card-text class="import-post-tags__card-content">
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.ImportPostTags.label.errorMessage') }}
            </span>
            {{ errorMessage.description }}
          </div>
        </v-list-item>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import cardsModel from '@/mixins/cards-model';
import SheetImporterTagsServiceV2 from '@/services/SheetImporterTagsServiceV2';

import { DsButton } from '@ds/components';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'ImportPostTags',
  components: {
    DsButton,
    GenericChart,
  },
  mixins: {
    cardsModel,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      file: null,
      dialog: false,
      requestStatus: 'success',
      isSending: false,
      valid: true,
      modal: false,
      rules: {
        isValue: [v => Boolean(v) || this.$t('components.ImportPostTags.label.requiredValue')],
      },
      rawResponse: null,
      success: null,
      error: false,
      errorMessage: null,
    };
  },
  computed: {
    infoLabel() {
      return this.$t('components.ImportPostTags.infoLabel');
    },
    closeLabel() {
      return this.$t('components.ImportPostTags.closeLabel');
    },
    ...mapState('user', [
      'user',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    disableButton() {
      return this.isSending || !this.file;
    },
  },
  methods: {
    ...mapActions('cards', [
      'getSheetImporterTagsService',
    ]),
    openDialog() {
      this.dialog = true;
    },
    createFormData() {
      const formData = new FormData();
      formData.append('file', this.file);
      return formData;
    },
    resetForm() {
      this.$refs.form.reset();
      this.file = null;
      this.success = false;
      this.error = false;
      this.clearResponses();
    },
    clearResponses() {
      this.rawResponse = null;
      this.errorMessage = null;
    },
    clearForm() {
      this.$refs.form.reset();
      this.file = null;
    },
    async postData() {
      this.isSending = true;
      this.clearResponses();
      const formData = this.createFormData();
      try {
        const sheetImporterTagsService = new SheetImporterTagsServiceV2(this.accountId, this.universeId);
        const response = await sheetImporterTagsService.importSheetToTagPosts(
          formData,
          this.getImportTagsOptions(),
        );
        this.success = true;
        this.clearForm();
        this.$noty.success(this.$t('components.ImportPostTags.upload.success'));
        this.rawResponse = response;
      } catch (e) {
        console.error(e);
        this.error = true;
        this.errorMessage = e.response?.data;
        this.$noty.error(this.$t('components.ImportPostTags.upload.error'));
      } finally {
        this.isSending = false;
      }
    },
    getImportTagsOptions() {
      return {
        accountId: this.accountId,
        universeId: this.universeId,
        postedAtColumn: 0,
        profileAndChannelColumn: 2,
        channelColumn: 3,
        pageIdColumn: 4,
        urlColumn: 5,
        tagsColumn: '6,7,8',
        importStatusColumn: 9,
        email: this.user.email,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  .import-post-tags {
    overflow: hidden;

    &__close-button {
      display: flex;
      flex: end;
      justify-content: flex-end;
    }

    &__card-content {
      padding: 1rem 0 0 0;
      display: flex;
      flex-direction: column;
      font-size: var(--ds-font-size-xs);
      line-height: 16px;
      overflow: hidden;

      .v-list-item {
        min-height: 2rem;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }
    }
  }

  ::v-deep {
    .v-input {
      display: flex;
    }
  }
</style>
